import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useLogout, useSession } from '@/lib/session';
import { useSellerOrders } from '@/lib/consoleQueries';
import { useSellerPharmacy } from '@/lib/onboardingQueries';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { Logo } from './BuyerLayout';

/**
 * The pharmacy counter console.
 *
 * ── WHY THE NAVIGATION IS AT THE BOTTOM ON A PHONE ──────────────────────────
 *
 * A pharmacist is holding a phone in one hand and a box of medicine in the
 * other. Anything above the middle of a 6-inch screen needs a second hand or a
 * shuffle of grip, and both mean putting the box down. On a tablet at a counter
 * the same nav moves to a rail, because the ergonomics invert entirely.
 *
 * ── WHY THE NEW-ORDER COUNT IS IN THE CHROME ────────────────────────────────
 *
 * The accept window is fifteen minutes and a missed one is a lost sale. The
 * count therefore follows the pharmacist onto every screen rather than living
 * only on the queue they may have navigated away from.
 */

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /**
   * A shorter label, for the phone bar only.
   *
   * At 320px a tab is 64px wide and the label is 11px, which fits about ten
   * characters. "Prescriptions" is thirteen and was still being clipped after
   * the bar was widened — the one word that did not fit. The rail, the sheet
   * and every screen keep the full word; only the tab abbreviates.
   */
  short?: string;
}

/**
 * A pharmacy that is not live yet has one job, and it is not taking orders.
 *
 * Showing Orders and Products to a draft pharmacy is four taps that all end in
 * "you cannot do this yet" — so before approval the nav is the checklist and
 * nothing else.
 */
function navFor(canSell: boolean, ownsBusiness: boolean): NavItem[] {
  if (!canSell) {
    return [{ to: '/seller/onboarding', label: 'Registration', icon: 'doc', end: true }];
  }

  return [
    { to: '/seller', label: 'Dashboard', icon: 'chart', end: true },
    { to: '/seller/orders', label: 'Orders', icon: 'orders' },
    { to: '/seller/listings', label: 'Products', icon: 'pill' },
    // ── THE STOCK ROOM ──────────────────────────────────────────────
    //
    // Beside Products, because one is what you sell and the other is what you
    // have. Everything behind it — the ledger, the costs, the expiry dates —
    // was built long before there was a door to it, so a seller could see
    // their listings and their orders and had no way to ask what their shelf
    // was worth.
    { to: '/seller/inventory', label: 'Inventory', icon: 'box' },
    // Buyers sending a prescription and asking what it costs. Sits beside
    // Orders rather than under Products, because it is work that arrives and
    // goes stale — a request answered tomorrow is usually a request lost.
    // "Scripts" in the phone tab: the trade's own word for them, and the only
    // label that does not fit a 320px tab at full length.
    { to: '/seller/prescriptions', label: 'Prescriptions', icon: 'doc', short: 'Scripts' },
    // Scan a pack, search the catalogue, or upload a whole stock file.
    { to: '/seller/add', label: 'Add stock', icon: 'grid' },
    // Deliveries, as documents. Stock arrives ON one of these — supplier,
    // invoice, cost, and who checked it in — which is what makes every figure
    // downstream worth reading.
    { to: '/seller/receiving', label: 'Deliveries', icon: 'truck' },
    // Batches pulled from sale. We take them off the listings automatically;
    // only a person can check the shelf.
    { to: '/seller/recalls', label: 'Recalls', icon: 'alert' },
    // "Income", not "Money": money is what the section is about, income is
    // the number a pharmacist came to look at. The dashboard answers "how am I
    // doing"; this answers "where exactly did that figure come from" — a
    // different screen and a different afternoon.
    { to: '/seller/money', label: 'Income', icon: 'money' },
    // What buyers have said, and the pharmacy's chance to answer BEFORE a
    // decision. Their first knowledge of a complaint used to be money missing
    // from a statement.
    { to: '/seller/complaints', label: 'Complaints', icon: 'message' },
    // Buying visibility for the shop or one product. Shown to everybody who
    // can sell, not only the owner: a manager who notices a supplement moving
    // is exactly the person who should be able to see what a placement costs.
    // BUYING it needs `adverts.buy`, which the API enforces.
    { to: '/seller/adverts', label: 'Advertising', icon: 'star' },
    // "Business", not "Pharmacy". This console serves veterinary clinics,
    // agrovet stores and farm suppliers as well, and a vet practice tapping a
    // tab labelled Pharmacy to reach its OWN record is being told, quietly and
    // repeatedly, that the software was not built for it.
    { to: '/seller/onboarding', label: 'Business', icon: 'store' },
    // The owner's own team, and only the owner's: a counter assistant shown a
    // Team tab that refuses them learns the console is lying to them.
    ...(ownsBusiness
      ? [{ to: '/seller/team', label: 'Team', icon: 'users' as const }]
      : []),
  ];
}

/**
 * The destinations that get a tab of their own on a phone.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  WHY FOUR, AND WHY A "MORE" INSTEAD OF THIRTEEN
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The bar gave every destination an equal share of the width. That was fine at
 * seven — the comment below it still says seven — and the console has since
 * grown to twelve plus Sign out. Measured on a real phone:
 *
 *     320px  ·  13 items  ·  25px each
 *     390px  ·  13 items  ·  30px each
 *
 * §11 asks for 44px. These are barely half of it, which is why every label
 * truncated to two characters — "Da… Or… Pr… Pr… A… D…" — with two of them
 * starting "Pr". A vendor could not read the bar, could not reliably hit a
 * target in it, and could not tell Products from Prescriptions.
 *
 * ── WHY NOT A SCROLLING BAR ───────────────────────────────────────────────
 *
 * The obvious alternative is to let the row scroll sideways. It keeps every
 * destination one tap away and hides most of them behind a gesture nobody is
 * told about — which is the exact complaint that started this: "there is no
 * toggle to go check out various menus". A sheet announces itself; a scroll
 * region does not.
 *
 * ── WHY THESE FOUR ────────────────────────────────────────────────────────
 *
 * Chosen by what it costs to miss, not by how often they are opened:
 *
 *   Orders         a fifteen-minute accept window. Missing one is a lost sale,
 *                  and it is the only item carrying a live count.
 *   Inventory      what is on the shelf and what it is worth. A vendor at the
 *                  counter with a customer asking "do you have it" is the
 *                  commonest reason this console is opened on a phone at all.
 *   Dashboard      the glance — what is waiting, what came in.
 *   Products       the daily work, and the thing a vendor opens to check a
 *                  price while standing in front of a customer.
 *
 * Everything else is deliberate, sit-down work: receiving a delivery, reading
 * a statement, buying a placement, editing the business record. Two taps is
 * the right price for those and the wrong price for a fifteen-minute clock.
 *
 * ── PRESCRIPTIONS IS THE ONE THAT HURTS ───────────────────────────────────
 *
 * It held a tab until Inventory arrived, on the grounds that a quote request
 * answered tomorrow is usually lost. It is in the sheet now, and the honest
 * reason is that there are four slots and five good candidates.
 *
 * What makes that survivable is that Prescriptions carries no count — a stale
 * request was already invisible from the bar, tab or no tab. The right fix is
 * a badge on it the way Orders has one, and that is worth more than the slot.
 */
const PHONE_TABS = ['/seller', '/seller/orders', '/seller/inventory', '/seller/listings'];

export function SellerLayout() {
  const { user } = useSession();
  const { data } = useSellerOrders();
  const pharmacy = useSellerPharmacy();
  const sellerNav = navFor(
    pharmacy.data?.pharmacy?.can_sell ?? false,
    user?.owns_business ?? false,
  );
  const logout = useLogout();
  const navigate = useNavigate();

  const newCount = data?.meta.counts?.new ?? 0;
  const location = useLocation();

  /**
   * Whether the sheet is up — DERIVED, not stored.
   *
   * ── WHY IT REMEMBERS A PATH AND NOT A BOOLEAN ──────────────────────────
   *
   * A sheet still standing over the screen somebody just navigated to is a
   * sheet they have to dismiss before they can read anything, so it has to
   * close on navigation. The obvious way is an effect watching the pathname
   * that calls `setMoreOpen(false)` — which works, and starts a second render
   * every time anybody moves anywhere in the console.
   *
   * Holding the path it was opened ON gets the same behaviour with no effect
   * at all: the moment the pathname changes, `openedAt` no longer matches and
   * the sheet is closed during render. It also covers the routes an effect on
   * a click handler would miss — browser back, a redirect, a deep link.
   */
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const moreOpen = openedAt === location.pathname;

  const setMoreOpen = (open: boolean) => setOpenedAt(open ? location.pathname : null);

  // Escape, because it is a dialog and that is what people press.
  useEffect(() => {
    if (!moreOpen) return;

    // `setOpenedAt`, not the `setMoreOpen` wrapper: the wrapper closes over
    // the current pathname and so is a new function every render, which would
    // mean tearing this listener down and rebuilding it on each one. The
    // setter from useState is stable for the life of the component.
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenedAt(null);
    };

    window.addEventListener('keydown', key);

    return () => window.removeEventListener('keydown', key);
  }, [moreOpen]);

  /**
   * The split, order preserved from `sellerNav` so the tabs read in the same
   * sequence as the rail a vendor sees on a tablet.
   *
   * ── WHY A SHORT NAV KEEPS EVERY DESTINATION AS A TAB ──────────────────
   *
   * A pharmacy awaiting approval has exactly one destination — its
   * registration — and the whitelist does not contain it, because after
   * approval that same route becomes "Business" and belongs in the sheet.
   * Filtering alone therefore put the ONE thing a pending vendor can do
   * behind a More button, which is the opposite of the problem being fixed.
   *
   * Four plus Sign out is 78px a slot at 390px, comfortably past the 44px
   * §11 asks for, so anything that fits gets a tab and the sheet does not
   * appear at all.
   */
  const tabs =
    sellerNav.length <= 4 ? sellerNav : sellerNav.filter((item) => PHONE_TABS.includes(item.to));

  const rest = sellerNav.filter((item) => !tabs.includes(item));

  const signOut = () =>
    logout.mutate(undefined, { onSettled: () => navigate('/seller/login', { replace: true }) });

  return (
    <div className="flex min-h-dvh flex-col bg-paper md:flex-row">
      {/* Counter rail — tablets and laptops. */}
      <nav
        aria-label="Seller"
        className="hidden w-[220px] shrink-0 flex-col gap-[3px] bg-ink p-3 pt-[18px] md:flex"
      >
        <span className="flex items-center gap-1.5 px-2 pb-[18px] font-display text-[17px] font-extrabold tracking-[-0.02em] text-white">
          <span className="grid h-[22px] w-[22px] place-items-center rounded-[7px] bg-white/15">
            <Logo className="h-[13px] w-[13px] fill-white" />
          </span>
          ChiCare
          <span className="text-[10px] font-medium tracking-[0.1em] opacity-60">SELLER</span>
        </span>

        {sellerNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-[9px] px-3 py-2.5 text-[13px] font-medium no-underline',
                isActive ? 'bg-white/[0.13] font-bold text-white' : 'text-[#B9CBEC] hover:text-white',
              )
            }
          >
            <Icon name={item.icon} size={17} strokeWidth={1.8} />
            {item.label}
            {item.to === '/seller/orders' && newCount > 0 ? (
              <span className="ml-auto rounded-full bg-caution px-1.5 py-0.5 font-mono text-[10px] font-bold text-ink">
                {newCount}
              </span>
            ) : null}
          </NavLink>
        ))}

        <div className="mt-auto px-2 pt-4">
          <p className="m-0 text-[12px] font-medium text-white">{user?.name}</p>
          <button
            type="button"
            onClick={signOut}
            className="mt-1 min-h-11 bg-transparent p-0 text-left text-[12px] text-[#B9CBEC] hover:text-white"
          >
            Sign out
          </button>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col pb-[76px] md:pb-0">
        {/* A slim bar for the bell. This console is a dark rail beside a white
            page and had nowhere to put one — and a seller is exactly who needs
            telling that an order is sitting on a fifteen-minute clock. */}
        <div className="flex items-center justify-end border-b border-line bg-white px-3 py-1.5">
          <NotificationBell />
        </div>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          THUMB RAIL — PHONES

          Four tabs and a More. `min-w-0` on every child, because a flex item
          defaults to `min-width: auto` and so refuses to shrink below its own
          content — and one element wider than the viewport widens the
          document, which switches off every media query below that width.

          See PHONE_TABS above for why it is four, and which four.
          ════════════════════════════════════════════════════════════════ */}
      <nav
        aria-label="Seller"
        data-testid="seller-tabs"
        className="fixed inset-x-0 bottom-0 z-20 flex border-t border-line bg-white md:hidden"
      >
        {tabs.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'relative flex min-h-[60px] min-w-0 flex-1 flex-col items-center justify-center gap-1 px-0.5 text-[11px] font-medium no-underline',
                isActive ? 'text-blue' : 'text-muted',
              )
            }
          >
            <Icon name={item.icon} size={20} strokeWidth={1.8} />
            <span className="w-full truncate text-center">{item.short ?? item.label}</span>
            {item.to === '/seller/orders' && newCount > 0 ? (
              <span className="absolute right-[22%] top-2 rounded-full bg-caution px-1.5 py-0.5 font-mono text-[10px] font-bold text-ink">
                {newCount}
              </span>
            ) : null}
          </NavLink>
        ))}

        {/* Only when there genuinely is more. A pharmacy awaiting approval has
            one destination, and a More button over an almost-empty sheet is a
            control that teaches people not to press it. */}
        {rest.length > 0 ? (
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            data-testid="seller-more"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            className={cn(
              'flex min-h-[60px] min-w-0 flex-1 flex-col items-center justify-center gap-1 bg-transparent px-0.5 text-[11px] font-medium',
              moreOpen ? 'text-blue' : 'text-muted',
            )}
          >
            <Icon name="grid" size={20} strokeWidth={1.8} />
            <span className="w-full truncate text-center">More</span>
          </button>
        ) : (
          /* Nothing else to reach, so the way out takes the slot rather than
             leaving a vendor on a one-tab bar with no visible sign-out. */
          <button
            type="button"
            onClick={signOut}
            className="flex min-h-[60px] min-w-0 flex-1 flex-col items-center justify-center gap-1 bg-transparent px-0.5 text-[11px] font-medium text-muted"
          >
            <Icon name="user" size={20} strokeWidth={1.8} />
            <span className="w-full truncate text-center">Sign out</span>
          </button>
        )}
      </nav>

      {/* ════════════════════════════════════════════════════════════════
          EVERYTHING ELSE

          A sheet from the bottom rather than a drawer from the side: the
          control that opens it sits at the bottom of the screen, and a panel
          that appears somewhere other than where you pressed is a panel people
          lose track of.

          Full labels, 44px rows, and the order count repeated beside anything
          carrying one — somebody who opens this is looking for a room, and the
          row should say whether there is anything waiting in it.
          ════════════════════════════════════════════════════════════════ */}
      {moreOpen ? (
        <div className="fixed inset-0 z-30 md:hidden" data-testid="seller-more-sheet">
          <button
            type="button"
            aria-label="Close the menu"
            onClick={() => setMoreOpen(false)}
            className="absolute inset-0 w-full cursor-default bg-ink/40"
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="More"
            className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-[18px] border-t border-line bg-white pb-[env(safe-area-inset-bottom)] shadow-lg"
          >
            <div className="sticky top-0 flex items-center justify-between border-b border-line-2 bg-white px-4 py-3">
              <span className="font-display text-[15px] font-bold text-ink">Everything else</span>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                aria-label="Close"
                className="-mr-2 grid h-11 w-11 place-items-center rounded-btn text-muted hover:text-ink"
              >
                <Icon name="x" size={18} strokeWidth={2.2} />
              </button>
            </div>

            <ul className="m-0 list-none p-2">
              {rest.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-11 items-center gap-3 rounded-[10px] px-2.5 py-2 text-[13.5px] font-medium no-underline',
                        isActive ? 'bg-blue-tint text-blue' : 'text-ink hover:bg-paper',
                      )
                    }
                  >
                    <Icon name={item.icon} size={18} strokeWidth={1.8} className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Below a rule and named, not an eleventh row of the same list.
                Signing out by mistake while reaching for Income costs a vendor
                their session and their place. */}
            <div className="border-t border-line-2 p-2">
              <p className="m-0 px-2.5 pb-1 text-[11px] font-semibold text-ink">{user?.name}</p>
              <button
                type="button"
                onClick={signOut}
                className="flex min-h-11 w-full items-center gap-3 rounded-[10px] bg-transparent px-2.5 py-2 text-left text-[13.5px] font-medium text-danger hover:bg-danger-tint"
              >
                <Icon name="back" size={18} strokeWidth={2} className="shrink-0" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
